import { useCallback, useRef, useState } from 'react';
import type { BillingInfoData } from '@shipstation/financial-services-ui-components';

import { pollForNewlyCreatedPaymentMethod } from '../../../financial-services-ui/poll-for-new-payment-method';
import type { PaymentMethodDetails } from '../../../financial-services-ui/types/fsui';

type UsePaymentMethodCreationProps = {
  previousPaymentMethodIds: string[];
  reloadData?: () => Promise<[unknown, PaymentMethodDetails[] | undefined]>;
  onSuccess: (paymentMethodId: string, billingInfo?: BillingInfoData, paymentMethod?: PaymentMethodDetails) => void;
  onError: () => void;
  onTimeout?: () => void;
};

export const usePaymentMethodCreation = ({
  previousPaymentMethodIds,
  reloadData,
  onSuccess,
  onError,
  onTimeout,
}: UsePaymentMethodCreationProps) => {
  const [isProcessing, setIsProcessing] = useState(false);

  // Use ref to track the latest billing info without causing re-renders
  const billingInfoRef = useRef<BillingInfoData | undefined>();

  const handlePaymentSuccess = useCallback(async () => {
    if (!reloadData) {
      onError();
      return;
    }

    setIsProcessing(true);

    try {
      // Poll for new payment method (10 retries × 3s = 30s max)
      const newPaymentMethod = await pollForNewlyCreatedPaymentMethod({
        reloadData,
        previousPaymentMethodIds,
      });

      if (!newPaymentMethod?.id) {
        // Polling ran to completion without ever seeing a new method -- this is the genuine
        // 30s-timeout case, distinct from reloadData()/mapping throwing for some other reason below.
        onTimeout?.();
        onError();
        return;
      }

      // Extract billing info from the payment method's accountHolderAddress
      // This is more reliable than FSUI context which is missing fields like 'state'
      let billingInfoFromPaymentMethod: BillingInfoData | undefined;

      if (newPaymentMethod?.accountHolderAddress) {
        // Use the helper to extract billing info from payment method
        const { getBillingInfoFromPaymentMethod } = await import('../../../financial-services-ui/mapping');

        const extracted = getBillingInfoFromPaymentMethod(newPaymentMethod, {
          name: '', // Will be populated from form
          homeCountry: 'US',
          phone: '', // Will be populated from form
          companyName: '', // Will be populated from form
        });

        billingInfoFromPaymentMethod = extracted ?? undefined;
      }

      // Notify parent with billing info extracted from payment method (has complete address including state)
      onSuccess(newPaymentMethod.id, billingInfoFromPaymentMethod, newPaymentMethod);
    } catch {
      // Error notification handled by onError callback.
      onError();
    } finally {
      setIsProcessing(false);
    }
  }, [reloadData, previousPaymentMethodIds, onSuccess, onError, onTimeout]);

  // Update billing info ref when it changes
  const updateBillingInfoRef = useCallback((billingInfo?: BillingInfoData) => {
    billingInfoRef.current = billingInfo;
  }, []);

  return {
    isProcessing,
    handlePaymentSuccess,
    updateBillingInfoRef,
  };
};
